import React, { useEffect, useRef, useState } from "react";
import {
  GetMarkets200Response,
  InstrumentTypeResponse,
  OptionTypeResponse,
} from "../../codegen-api";
import { getDefaultInstrumentType } from "../../config/features";
import { IOptionMarket } from "./useGetMarkets";
import { IMarket } from "../MarketContext";
import { IMarketInstrumentContextProviderProps } from "./types";

interface IUseParamSyncArgs {
  params: IMarketInstrumentContextProviderProps["params"];
  onAssetDerivativeChanged: IMarketInstrumentContextProviderProps["onAssetDerivativeChanged"];
  market: IMarket;
  setMarket: React.Dispatch<React.SetStateAction<IMarket>>;
  activeOptionMarkets?: GetMarkets200Response[];
  activePerpMarkets?: GetMarkets200Response[];
  activeSpotMarkets?: GetMarkets200Response[];
  fallbackMarket: IMarket;
  marketsData?: GetMarkets200Response[];
  setStrike: React.Dispatch<React.SetStateAction<string | undefined>>;
  setExpiry: React.Dispatch<React.SetStateAction<number | undefined>>;
  setOrderType: React.Dispatch<
    React.SetStateAction<OptionTypeResponse | undefined>
  >;
  selectedOptionInstrument: IOptionMarket | undefined;
  selectedPerpetualInstrument: GetMarkets200Response | undefined;
  selectedSpotInstrument: GetMarkets200Response | undefined;
}

export function useParamSync({
  params,
  onAssetDerivativeChanged,
  market,
  setMarket,
  activeOptionMarkets,
  activePerpMarkets,
  activeSpotMarkets,
  fallbackMarket,
  marketsData,
  setStrike,
  setExpiry,
  setOrderType,
  selectedOptionInstrument,
  selectedPerpetualInstrument,
  selectedSpotInstrument,
}: IUseParamSyncArgs) {
  /**
   * These flags are "did this one-shot sync already run", and they are read in
   * two different ways, which is why each is both a ref and a state.
   *
   * The ref is the GUARD. It is what the effects below test and set, and it
   * updates synchronously — so an effect that re-runs before React has
   * committed the state flip still sees "already processed" and cannot run the
   * body twice.
   *
   * The state is the RENDER TRIGGER, read only by effect 3, which must re-run
   * once both syncs have completed.
   *
   * The guards used to be state ALONE, tested inside a `setX(prev => ...)`
   * updater so the current value could be read without adding it to the
   * dependency array. That worked, but state updaters run during the RENDER
   * phase and must be pure: the updater below called `setMarket`, whose
   * `useState` lives in `MarketContextProvider`, so every param sync updated
   * one component while React was rendering another. React said so out loud —
   * "Cannot update a component (MarketContextProvider) while rendering a
   * different component (MarketInstrumentContextProvider)" — and the
   * `console-health` browser spec failed on it.
   *
   * That warning is development-only, but the impurity is not: StrictMode
   * invokes an updater twice, which fired `setMarket` (and the three option
   * setters below) twice per sync, and concurrent rendering is free to discard
   * and replay an updater. A ref guard plus setters called from the effect
   * BODY is the supported shape and is idempotent by construction.
   */
  const assetDerivativeProcessedRef = useRef(false);
  const instrumentProcessedRef = useRef(false);
  const [assetDerivativeParamProcessed, setAssetDerivativeParamProcessed] =
    useState(false);
  const [instrumentParamsProcessed, setInstrumentParamsProcessed] =
    useState(false);

  // 1. Changing of params should set the market
  useEffect(() => {
    if (!params) {
      return;
    }
    const { asset, derivative } = params;

    // Wait until the relevant market type data is loaded before processing.
    // Keyed as a full Record so a new instrument type has to be wired in here.
    const marketsByInstrument: Record<
      InstrumentTypeResponse,
      GetMarkets200Response[] | undefined
    > = {
      [InstrumentTypeResponse.Option]: activeOptionMarkets,
      [InstrumentTypeResponse.Perpetual]: activePerpMarkets,
      [InstrumentTypeResponse.Spot]: activeSpotMarkets,
    };
    const relevantMarkets = derivative
      ? marketsByInstrument[derivative]
      : marketsByInstrument[getDefaultInstrumentType()];

    if (!relevantMarkets?.length) {
      return; // Don't process until the correct market type is loaded
    }

    if (assetDerivativeProcessedRef.current) {
      return;
    }
    assetDerivativeProcessedRef.current = true;

    setMarket((prev) => {
      if (!derivative && !asset) {
        return prev.asset === fallbackMarket.asset &&
          prev.derivative === fallbackMarket.derivative
          ? prev
          : fallbackMarket;
      }

      if (derivative !== prev.derivative || asset !== prev.asset) {
        const assetValid = relevantMarkets.some((m) => m.underlying_asset === asset);
        return {
          // An absent derivative falls back to the default vertical — the
          // same default used by the router and MarketContext. (This used to
          // default to OPTION, which disagreed with every other call site.)
          derivative: derivative || getDefaultInstrumentType(),
          asset: asset && assetValid ? asset : fallbackMarket.asset,
        };
      }
      return prev;
    });
    setAssetDerivativeParamProcessed(true);
  }, [
    activeOptionMarkets,
    activePerpMarkets,
    activeSpotMarkets,
    fallbackMarket,
    params,
    setMarket,
  ]);

  // 2. Auto selects instrument if exist in params, and marketsData is loaded
  useEffect(() => {
    if (!params) {
      return;
    }
    if (instrumentProcessedRef.current) {
      return;
    }
    // Stay unprocessed until the market list arrives, so the instrument param
    // is still applied on the render where it can actually be resolved.
    if (!marketsData) {
      return;
    }
    instrumentProcessedRef.current = true;

    const { instrument } = params;
    const marketInstrument = marketsData.find(
      (m) => m.instrument_name === instrument
    );
    // Only applies to options.
    if (
      marketInstrument?.expiry &&
      marketInstrument.strike &&
      marketInstrument.option_type
    ) {
      // Three independent setters, called flat. They were previously nested
      // inside one another's updaters purely to sequence them, which is not
      // what an updater is for — React batches these three regardless, and the
      // nesting made each one fire twice under StrictMode.
      setStrike(marketInstrument.strike);
      setOrderType(marketInstrument.option_type);
      setExpiry(Number(marketInstrument.expiry));
    }
    setInstrumentParamsProcessed(true);
  }, [marketsData, params, setExpiry, setOrderType, setStrike]);

  // 3. Changing of markets and instrument should set the route, but only AFTER the params are processed
  useEffect(() => {
    const { asset, derivative } = market;
    if (
      asset &&
      derivative &&
      assetDerivativeParamProcessed &&
      instrumentParamsProcessed
    ) {
      const selectedInstrument =
        selectedOptionInstrument || selectedPerpetualInstrument || selectedSpotInstrument;
      onAssetDerivativeChanged(
        asset,
        derivative,
        selectedInstrument?.instrument_name
      );
    }
  }, [
    assetDerivativeParamProcessed,
    instrumentParamsProcessed,
    market,
    selectedOptionInstrument,
    selectedPerpetualInstrument,
    selectedSpotInstrument,
    onAssetDerivativeChanged,
  ]);
}
